import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  getLostItemCase, lostItemStatusBadge,
  riderOfferReturnFee, riderConfirmReceipt, riderRequestReturnRefund, riderCancelLostItemReport,
  getDisputeConfig,
} from '../../services/disputeService';
import toast from 'react-hot-toast';
import BackButtonBottom from '../../components/common/BackButtonBottom';

export default function LostItemDetail() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [actionInProgress, setActionInProgress] = useState(false);

  // Action-specific UI state
  const [feeInput, setFeeInput] = useState('');
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [showRefundDialog, setShowRefundDialog] = useState(false);
  const [refundReason, setRefundReason] = useState('');

  const load = () => {
    setLoading(true);
    getLostItemCase(caseId)
      .then(setCaseData)
      .catch((err) => toast.error(err.message || 'Could not load case'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [caseId]);
  useEffect(() => { getDisputeConfig().then(setConfig).catch(() => {}); }, []);

  const fmtDate = (ts) => {
    if (!ts) return '';
    const d = ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts);
    return isNaN(d.getTime()) ? '' : d.toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const handleOfferFee = async () => {
    const fee = parseFloat(feeInput);
    if (isNaN(fee) || fee < (config?.lostItem?.MIN_RETURN_FEE || 30)) {
      toast.error(`Minimum fee is $${config?.lostItem?.MIN_RETURN_FEE || 30}`);
      return;
    }
    setActionInProgress(true);
    try {
      await riderOfferReturnFee({ caseId, fee });
      toast.success(`Offered $${fee.toFixed(2)} to driver. Waiting for them to accept.`);
      setFeeInput('');
      load();
    } catch (err) {
      toast.error(err.message || 'Failed to offer fee');
    }
    setActionInProgress(false);
  };

  const handleConfirmReceipt = async () => {
    if (!window.confirm('Confirm you received the item? This will release the payment to your driver.')) return;
    setActionInProgress(true);
    try {
      await riderConfirmReceipt({ caseId });
      toast.success('Receipt confirmed. Driver has been paid.');
      load();
    } catch (err) {
      toast.error(err.message || 'Failed');
    }
    setActionInProgress(false);
  };

  const handleRequestRefund = async () => {
    if (!refundReason || refundReason.trim().length < 10) {
      toast.error('Please provide a reason (at least 10 characters)');
      return;
    }
    setActionInProgress(true);
    try {
      await riderRequestReturnRefund({ caseId, reason: refundReason });
      toast.success('Refund request submitted. Admin will review within 24 hours.');
      setShowRefundDialog(false);
      setRefundReason('');
      load();
    } catch (err) {
      toast.error(err.message || 'Failed');
    }
    setActionInProgress(false);
  };

  const handleCancel = async () => {
    setActionInProgress(true);
    try {
      await riderCancelLostItemReport({ caseId, reason: cancelReason || null });
      toast.success('Report cancelled.');
      navigate('/rider/lost-items');
    } catch (err) {
      toast.error(err.message || 'Failed');
    }
    setActionInProgress(false);
  };

  if (loading) return <div className="p-10 text-center text-muted">Loading...</div>;
  if (!caseData) return <div className="p-10 text-center text-muted">Case not found</div>;

  const badge = lostItemStatusBadge(caseData.status);
  const isTerminal = ['resolved_returned', 'resolved_collected', 'unresolved',
    'expired_driver_custody', 'unresolved_pickup_timeout', 'cancelled_by_rider', 'admin_resolved'].includes(caseData.status);
  const minFee = config?.lostItem?.MIN_RETURN_FEE || 30;
  const maxFee = config?.lostItem?.MAX_RETURN_FEE || 200;

  return (
    <div className="max-w-[700px] mx-auto p-6">
      <div className="mb-4">
        <button onClick={() => navigate('/rider/lost-items')} className="bg-transparent border-0 text-brand-primary font-semibold text-[0.9rem] cursor-pointer p-0">← All lost items</button>
      </div>

      {/* Header */}
      <div className="flex justify-between items-start mb-4">
        <div>
          <h1 className="font-serif m-0">{caseData.referenceNumber}</h1>
          <div className="text-[0.78rem] text-muted mt-1">
            Reported {fmtDate(caseData.reportedAt || caseData.createdAt)}
          </div>
        </div>
        <span className="px-3 py-1 rounded-full text-[0.78rem] font-bold" style={{ background: badge.bg, color: badge.color }}>
          {badge.label}
        </span>
      </div>

      {/* Item details */}
      <div className="p-4 rounded-2xl bg-white border border-slate-200 mb-4">
        <div className="text-[0.78rem] text-muted font-semibold uppercase mb-1.5">The item</div>
        <div className="text-[0.95rem] text-brand-dark mb-2">{caseData.description}</div>
        <div className="text-[0.82rem] text-body">
          Location: <strong>{caseData.locationInVehicle?.replace(/_/g, ' ')}</strong>
        </div>

        {/* Rider's reported photos */}
        {caseData.reportPhotos?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {caseData.reportPhotos.map((url, i) => (
              <img key={i} src={url} alt={`Reported ${i + 1}`}
                className="w-[70px] h-[70px] object-cover rounded-lg border border-slate-200" />
            ))}
          </div>
        )}
      </div>

      {/* Driver response section (shown if driver has responded) */}
      {caseData.driverResponse && (
        <div className="p-4 rounded-2xl bg-white border border-slate-200 mb-4">
          <div className="text-[0.78rem] text-muted font-semibold uppercase mb-1.5">Driver response</div>
          <div className="text-[0.9rem] text-brand-dark mb-1">
            <strong>{caseData.driverName}</strong> said:{' '}
            {caseData.driverResponse === 'found_will_return' && 'I have the item and will return it.'}
            {caseData.driverResponse === 'found_needs_pickup' && 'I have the item but need you to collect it.'}
            {caseData.driverResponse === 'not_found' && 'I checked the vehicle and did not find the item.'}
          </div>
          {caseData.driverNotes && (
            <div className="text-[0.85rem] text-body italic mt-1.5">
              "{caseData.driverNotes}"
            </div>
          )}
          {caseData.driverPhotos?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2.5">
              {caseData.driverPhotos.map((url, i) => (
                <img key={i} src={url} alt={`Driver ${i + 1}`}
                  className="w-[70px] h-[70px] object-cover rounded-lg border border-slate-200" />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── STATE-DRIVEN ACTIONS ─── */}

      {/* State: reported — waiting for driver */}
      {caseData.status === 'reported' && (
        <div className="p-4 rounded-2xl mb-4 text-[0.9rem]" style={{ background: '#fef3c7', border: '1px solid #fde68a', color: '#92400e' }}>
          Your driver has been notified. They have 48 hours to respond. We'll let you know when they do.
        </div>
      )}

      {/* State: driver_found_will_return — offer return fee */}
      {caseData.status === 'driver_found_will_return' && (
        <div className="p-4 rounded-2xl bg-white mb-4" style={{ border: '1.5px solid #2dd4a8' }}>
          <h3 className="mt-0 mb-2 text-brand-dark">Offer a return fee</h3>
          <p className="text-body text-[0.85rem] mb-3">
            Your driver has the item. Offer them a return fee (minimum ${minFee}, maximum ${maxFee})
            to have them deliver it to you. You can also decline to pay a return fee and instead
            arrange to collect it yourself.
          </p>
          <div className="flex gap-2 items-stretch">
            <input type="number" className="form-input flex-1"
              placeholder={`Amount (min $${minFee})`}
              min={minFee} max={maxFee} step="5"
              value={feeInput} onChange={(e) => setFeeInput(e.target.value)} />
            <button onClick={handleOfferFee} disabled={actionInProgress || !feeInput}
              className="px-4 py-2.5 rounded-[10px] border-0 bg-brand-primary text-white cursor-pointer font-bold">Offer fee</button>
          </div>
          <div className="text-[0.78rem] text-muted mt-2">
            Your card will only be charged once your driver accepts the offered amount.
          </div>
        </div>
      )}

      {/* State: awaiting_driver_fee_confirmation */}
      {caseData.status === 'awaiting_driver_fee_confirmation' && (
        <div className="p-4 rounded-2xl mb-4 text-[0.9rem]" style={{ background: '#fef3c7', border: '1px solid #fde68a', color: '#92400e' }}>
          You offered <strong>${caseData.returnFeeOffered?.toFixed(2)}</strong>. Waiting for your driver to accept.
        </div>
      )}

      {/* State: driver_found_needs_pickup or awaiting_pickup_coordination */}
      {(caseData.status === 'driver_found_needs_pickup' || caseData.status === 'awaiting_pickup_coordination') && (
        <div className="p-4 rounded-2xl mb-4" style={{ background: '#dbeafe', border: '1px solid #bfdbfe' }}>
          <h3 className="mt-0 mb-2" style={{ color: '#1e40af' }}>Pickup coordination</h3>
          <p className="text-[0.85rem] mb-2" style={{ color: '#1e40af' }}>
            Your driver needs you to collect the item. Use in-app messages on the original trip
            to agree on a pickup location and time, then book a delivery trip to that location.
          </p>
          {caseData.bookingId && (
            <button onClick={() => navigate(`/rider/trip/${caseData.bookingId}`)}
              className="px-3.5 py-2 rounded-lg bg-white cursor-pointer font-semibold text-[0.85rem]"
              style={{ border: '1.5px solid #1e40af', color: '#1e40af' }}>Open trip messages</button>
          )}
          <div className="text-[0.78rem] mt-2" style={{ color: '#1e40af' }}>
            Driver custody expires {fmtDate(caseData.custodyDeadline)}. After that, the driver may dispose of the item.
          </div>
        </div>
      )}

      {/* State: return_paid — driver authorized */}
      {caseData.status === 'return_paid' && (
        <div className="p-4 rounded-2xl mb-4" style={{ background: '#f0fdfa', border: '1px solid #99f6e4' }}>
          <h3 className="mt-0 mb-2" style={{ color: '#0a8270' }}>Return authorized</h3>
          <p className="text-[0.85rem] mb-1" style={{ color: '#0a8270' }}>
            You paid <strong>${caseData.returnFeeOffered?.toFixed(2)}</strong>. Your driver has 72 hours to deliver.
          </p>
          <div className="text-[0.78rem]" style={{ color: '#0a8270' }}>
            Delivery deadline: {fmtDate(caseData.returnDeliveryDeadline)}
          </div>
        </div>
      )}

      {/* State: return_delivered_by_driver — confirm receipt */}
      {caseData.status === 'return_delivered_by_driver' && (
        <div className="p-4 rounded-2xl bg-white mb-4" style={{ border: '1.5px solid #2dd4a8' }}>
          <h3 className="mt-0 mb-2">Did you receive your item?</h3>
          <p className="text-body text-[0.85rem] mb-3">
            Your driver marked the item as delivered. Confirm receipt to release the payment of
            ${caseData.returnFeeOffered?.toFixed(2)}, or request a refund if it wasn't actually delivered.
          </p>
          <div className="flex gap-2 mb-2">
            <button onClick={handleConfirmReceipt} disabled={actionInProgress}
              className="flex-1 p-3 rounded-[10px] border-0 bg-brand-primary text-white cursor-pointer font-bold">✓ Confirm receipt</button>
            <button onClick={() => setShowRefundDialog(true)} disabled={actionInProgress}
              className="flex-1 p-3 rounded-[10px] bg-white cursor-pointer font-semibold"
              style={{ border: '1.5px solid #ff6b6b', color: '#ff6b6b' }}>Request refund</button>
          </div>
          <div className="text-[0.78rem] text-muted">
            If you don't respond by {fmtDate(caseData.riderConfirmDeadline)}, the payment will be released automatically.
          </div>
        </div>
      )}

      {/* Refund dialog */}
      {showRefundDialog && (
        <div className="p-4 rounded-2xl bg-white mb-4" style={{ border: '1px solid #fecaca' }}>
          <h3 className="mt-0 mb-2" style={{ color: '#dc2626' }}>Request refund</h3>
          <textarea className="form-input mb-2" rows={3}
            placeholder="Explain what happened — at least 10 characters"
            value={refundReason} onChange={(e) => setRefundReason(e.target.value)} />
          <div className="flex gap-2">
            <button onClick={() => { setShowRefundDialog(false); setRefundReason(''); }}
              className="flex-1 p-2.5 rounded-[10px] border-[1.5px] border-slate-200 bg-white text-body cursor-pointer font-semibold">Cancel</button>
            <button onClick={handleRequestRefund} disabled={actionInProgress}
              className="flex-1 p-2.5 rounded-[10px] border-0 text-white cursor-pointer font-bold"
              style={{ background: '#dc2626' }}>Submit refund request</button>
          </div>
        </div>
      )}

      {/* State: driver_not_found */}
      {caseData.status === 'driver_not_found' && (
        <div className="p-4 rounded-2xl mb-4 text-[0.9rem]" style={{ background: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b' }}>
          Your driver checked the vehicle and did not find the item. Our team has been notified and will investigate.
        </div>
      )}

      {/* State: admin_review_refund */}
      {caseData.status === 'admin_review_refund' && (
        <div className="p-4 rounded-2xl mb-4 text-[0.9rem]" style={{ background: '#fef3c7', border: '1px solid #fde68a', color: '#92400e' }}>
          Your refund request is under review. We'll respond within 24 hours.
        </div>
      )}

      {/* Terminal states */}
      {isTerminal && (
        <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 mb-4 text-[0.9rem] text-body">
          <strong>Resolution:</strong> {caseData.resolutionType || caseData.status}
          {caseData.resolutionNotes && <div className="mt-1">{caseData.resolutionNotes}</div>}
          {caseData.resolvedAt && <div className="mt-1 text-[0.82rem] text-muted">Resolved {fmtDate(caseData.resolvedAt)}</div>}
        </div>
      )}

      {/* Cancel option (only for non-terminal pre-payment states) */}
      {!isTerminal && !['return_paid', 'return_in_progress', 'return_delivered_by_driver', 'admin_review_refund'].includes(caseData.status) && (
        <>
          {!showCancelDialog ? (
            <button onClick={() => setShowCancelDialog(true)}
              className="w-full p-3 rounded-xl border-[1.5px] border-slate-200 bg-white text-body cursor-pointer font-semibold">Cancel this report</button>
          ) : (
            <div className="p-4 rounded-2xl bg-white" style={{ border: '1px solid #fecaca' }}>
              <h3 className="mt-0 mb-2" style={{ color: '#dc2626' }}>Cancel report?</h3>
              <p className="text-body text-[0.85rem] mb-2.5">
                This will close the case permanently.
              </p>
              <input className="form-input mb-2" placeholder="Reason (optional)"
                value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
              <div className="flex gap-2">
                <button onClick={() => { setShowCancelDialog(false); setCancelReason(''); }}
                  className="flex-1 p-2.5 rounded-[10px] border-[1.5px] border-slate-200 bg-white text-body cursor-pointer font-semibold">Keep case</button>
                <button onClick={handleCancel} disabled={actionInProgress}
                  className="flex-1 p-2.5 rounded-[10px] border-0 text-white cursor-pointer font-bold"
                  style={{ background: '#dc2626' }}>Confirm cancel</button>
              </div>
            </div>
          )}
        </>
      )}
    <BackButtonBottom to="/rider/lost-items" label="All lost items" />
    </div>
  );
}